/**
 * Shared retry layer for backend requests that fail below the HTTP
 * layer or with a temporary server error.
 *
 * Chrome tears down every live socket when the operating system
 * reports a network change (Wi-Fi roam, DHCP lease renewal, IP change,
 * VPN up/down) and fails the requests that were in flight with
 * `net::ERR_NETWORK_CHANGED`. Such a request surfaces to `fetch()` as a
 * rejected promise — or, when the change lands while the response body
 * is streaming, as a rejected `.blob()`/`.json()` — and the browser does
 * not restart it. The slower the link, the longer each request is in
 * flight and the more likely it is to be caught.
 *
 * These failures are transient: the next attempt over a freshly
 * established connection usually succeeds. Every backend transport in
 * the app routes its requests through the helpers below so the policy
 * — what is transient, how long to keep trying, how to back off — is
 * defined once.
 *
 * Retrying is bounded by time rather than by a fixed attempt count: a
 * network switch takes anywhere from a second to half a minute, and an
 * attempt budget sized for the short case gives up in the middle of the
 * long one. A caller's own deadline always bounds the retrying too —
 * retries run inside it, never past it.
 *
 * Every attempt is also bounded in time, so no backend call can hang:
 * building the request (which may wait on a token refresh) is bounded
 * by the response timeout, the wait for the response headers by the
 * response timeout plus an allowance that grows with the size of the
 * upload, and once the headers are in, an inactivity timeout fails the
 * attempt if the body stops arriving.
 * A transfer of any size completes as long as bytes keep flowing; a
 * socket that died silently is detected within the inactivity timeout.
 */

import { beginRetrying, RetryOutcome } from './connectionState';
import { startDeadline } from './requestDeadline';
import { abortError, isAbortError, sleep, throwIfAborted } from './retryOn202';

/**
 * How long a request keeps being retried, measured from its first
 * failure. Long enough to ride out a Wi-Fi switch or a VPN reconnect;
 * short enough that a genuine outage is reported while the user is
 * still waiting on the result. Measured from the failure rather than
 * from the first attempt so a long upload that fails minutes in still
 * gets its retries.
 */
export const RETRY_WINDOW_MS = 45_000;

/**
 * Backstop on attempts within the window, so a failure that returns
 * instantly cannot spin; the backoff schedule alone would allow ~8.
 */
const MAX_ATTEMPTS = 12;

/** Backoff before the first retry; doubles per attempt up to the cap. */
const INITIAL_BACKOFF_MS = 1_000;

/** Cap for a single backoff wait (and for an honoured `Retry-After`). */
const MAX_BACKOFF_MS = 8_000;

/**
 * Statuses describing a temporarily unavailable or overloaded server.
 * Every other 4xx is a client-side defect that would fail identically
 * on every retry, so it is never retried.
 */
const RETRYABLE_HTTP_STATUS_CODES: ReadonlySet<number> = new Set([
    408, // Request Timeout
    425, // Too Early
    429, // Too Many Requests
    500, // Internal Server Error
    502, // Bad Gateway
    503, // Service Unavailable
    504, // Gateway Timeout
]);

/** Statuses whose `Retry-After` header is meaningful for a retry. */
const RETRY_AFTER_STATUS_CODES: ReadonlySet<number> = new Set([429, 503]);

/**
 * How long an attempt may wait for its response headers, before any
 * allowance for the upload ({@link responseTimeoutFor}). Also bounds
 * building the request, which may wait on a token refresh. Generous
 * because some requests make the server do real work before it can
 * answer — assembling a whole series into one archive, say — and a
 * timeout that fires on such a request fires again on every retry.
 * Requests known to answer quickly set a tighter one.
 */
export const DEFAULT_RESPONSE_TIMEOUT_MS = 120_000;

/**
 * Most uploads a caller keeps in flight at once. Uploads running together
 * share the link, and the response timeout gives each only its share of it
 * ({@link MIN_UPLOAD_BYTES_PER_SECOND}); with more running at once, a slow
 * but working upload could time out and be failed.
 */
export const MAX_CONCURRENT_UPLOADS = 4;

/**
 * Upload rate the response timeout allows for, in bytes per second: one
 * {@link MAX_CONCURRENT_UPLOADS}th of a link slower than any a clinical
 * workstation should be on (~0.5 Mbit/s), so a slow but working upload is
 * never cut off, while the bound stays finite.
 */
const MIN_UPLOAD_BYTES_PER_SECOND = (64 * 1024) / MAX_CONCURRENT_UPLOADS;

/**
 * Longest silence tolerated while a response body streams. Bounds a
 * download by inactivity rather than by total duration, so a large
 * series on a slow link completes while a dead socket is still caught.
 */
export const BODY_IDLE_TIMEOUT_MS = 60_000;

/**
 * Spread for retries the `online` event wakes together. One network
 * change fails every in-flight request at once; waking them all in the
 * same instant would reconnect them in lockstep.
 */
const ONLINE_WAKE_SPREAD_MS = 1_000;

/** Statuses the Fetch API forbids a body for. */
const NULL_BODY_STATUSES: ReadonlySet<number> = new Set([204, 205, 304]);

/**
 * Per-request retry choice. `'transient'` retries transport failures
 * and temporary server errors; `'none'` sends exactly one attempt.
 */
export type RetryPolicy = 'transient' | 'none';

/**
 * Raised by the API transports when a request never produced an HTTP
 * response (connection reset, network change, DNS failure, …), as
 * opposed to a response carrying an error status. Messages never carry
 * PHI.
 */
export class NetworkTransportError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = 'NetworkTransportError';
    }
}

/**
 * The request could not be built, or the browser refused it before it
 * reached the network: a URL that does not parse, an unserializable body,
 * a header value that is not a valid byte string. Deterministic, so never
 * retried and never reported as a network failure. The message is fixed —
 * the original can echo the offending URL or header value, which may carry
 * identifiers.
 */
export class RequestConstructionError extends Error {
    /**
     * @param cause - The platform error, kept for the error report (it is
     *     not part of the message, which is what reaches the console).
     */
    public constructor(cause?: unknown) {
        super('The request could not be built (invalid URL, header or body).', {
            cause,
        });
        this.name = 'RequestConstructionError';
    }
}

/**
 * The server answered with an error status. Carries the status, which
 * {@link isTransientFailure} reads to tell a temporary server condition
 * from a definitive answer. The message names only the request and the
 * status, never a URL or a body.
 */
export class HttpStatusError extends Error {
    public constructor(
        public readonly status: number,
        what: string
    ) {
        super(`${what} failed with status ${status}.`);
        this.name = 'HttpStatusError';
    }
}

/**
 * A response whose status is worth retrying, carried as a throwable with
 * what the attempt's body reader made of it.
 */
class RetryableResponseError extends Error {
    public readonly response: Response;
    public readonly value: unknown;

    public constructor(response: Response, value: unknown) {
        super(`Retryable server response (${response.status}).`);
        this.name = 'RetryableResponseError';
        this.response = response;
        this.value = value;
    }
}

export type FetchRequest = {
    url: string;
    /**
     * The request. Its `signal`, when set, cancels the attempt; it
     * defaults to the `signal` of the retry options.
     */
    init: RequestInit;
};

export type RetryAttemptInfo = {
    /** 1-based index of the attempt that just failed. */
    attempt: number;
    /** Wait before the next attempt starts, in milliseconds. */
    delayMs: number;
    /** The failure that triggered the retry. */
    error: unknown;
};

export type TransientRetryOptions = {
    /**
     * How long retries may continue, measured from the first failure;
     * {@link RETRY_WINDOW_MS}. No retry is scheduled whose backoff would
     * end past the window.
     */
    retryWindowMs?: number;
    /** Attempts including the first; 1 sends exactly one attempt. */
    maxAttempts?: number;
    /** Cancels both the waiting and any further attempt. */
    signal?: AbortSignal;
    /** Decides whether a failure is worth retrying. */
    isTransient?: (error: unknown) => boolean;
    /** Observability hook, invoked once per scheduled retry. */
    onRetry?: (info: RetryAttemptInfo) => void;
    /** Jitter source in [0, 1). Injectable so tests stay deterministic. */
    randomFraction?: () => number;
    /** Wait implementation. Injectable so tests do not sleep. */
    sleep?: (
        delayMs: number,
        signal?: AbortSignal,
        options?: BackoffSleepOptions
    ) => Promise<void>;
    /** Clock in milliseconds. Injectable so tests control the window. */
    now?: () => number;
};

export type FetchRetryOptions = Omit<
    TransientRetryOptions,
    'maxAttempts' | 'isTransient'
> & {
    /**
     * A PHI-free name for the request in the retry log: its method and
     * URL template (`GET /api/patients/{id}`), or a fixed phrase. Never a
     * resolved URL — paths and queries carry DICOM UIDs and patient ids.
     */
    label: string;
    /**
     * Whether a failed attempt may be repeated; `'transient'` when
     * omitted. Transports pass {@link defaultRetryPolicy} unless their
     * call site chose.
     */
    retryPolicy?: RetryPolicy;
    /**
     * How long one attempt may wait for its response headers, before
     * the upload allowance is added; {@link DEFAULT_RESPONSE_TIMEOUT_MS}.
     * Expiry fails only that attempt, with a `TimeoutError`, which counts
     * as transient — a request stuck on a socket that died during a
     * network change would otherwise hang until the browser's own
     * multi-minute timeout.
     */
    responseTimeoutMs?: number;
};

/** The error an attempt fails with when one of its timeouts expires. */
function timeoutError(what: string, timeoutMs: number): DOMException {
    return new DOMException(
        `${what} timed out after ${timeoutMs} ms.`,
        'TimeoutError'
    );
}

/**
 * Size of a request body in bytes, as far as it can be known before
 * sending: 0 for no body and for a stream. A string counts one byte per
 * UTF-16 unit, which only matters for small JSON bodies.
 */
function bodySizeBytes(body: BodyInit | null | undefined): number {
    if (body === null || body === undefined) {
        return 0;
    }
    if (typeof body === 'string') {
        return body.length;
    }
    if (body instanceof Blob) {
        return body.size;
    }
    if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
        return body.byteLength;
    }
    if (body instanceof URLSearchParams) {
        return body.toString().length;
    }
    if (body instanceof FormData) {
        let size = 0;
        body.forEach((value) => {
            size += typeof value === 'string' ? value.length : value.size;
        });
        return size;
    }
    return 0;
}

/**
 * The response timeout for one attempt: `baseMs` plus the time the
 * upload may legitimately take at {@link MIN_UPLOAD_BYTES_PER_SECOND},
 * since the response headers cannot arrive before the body has been
 * sent.
 *
 * @param body - The request body.
 * @param baseMs - Allowance for connecting and for the server's work.
 */
export function responseTimeoutFor(
    body: BodyInit | null | undefined,
    baseMs: number = DEFAULT_RESPONSE_TIMEOUT_MS
): number {
    const uploadMs = Math.ceil(
        (bodySizeBytes(body) / MIN_UPLOAD_BYTES_PER_SECOND) * 1_000
    );
    return baseMs + uploadMs;
}

/** True for the expiry of a per-attempt deadline. */
function isTimeoutError(error: unknown): boolean {
    return (
        (error instanceof DOMException || error instanceof Error) &&
        error.name === 'TimeoutError'
    );
}

/**
 * True when the failure happened below the HTTP layer and a retry over
 * a new connection has a genuine chance of succeeding.
 */
export function isTransientNetworkError(error: unknown): boolean {
    if (isAbortError(error)) {
        // A deliberate cancellation must never be retried.
        return false;
    }
    if (error instanceof NetworkTransportError || isTimeoutError(error)) {
        return true;
    }
    // `fetch` rejects with a `TypeError` for every transport-level
    // failure: ERR_NETWORK_CHANGED, ERR_CONNECTION_RESET, DNS failures
    // and the like. The API deliberately exposes no error code, so
    // this is as specific as the platform allows.
    return error instanceof TypeError;
}

/** True for statuses that describe a temporary server-side condition. */
export function isRetryableHttpStatus(status: number): boolean {
    return RETRYABLE_HTTP_STATUS_CODES.has(status);
}

/**
 * True for a failure a later attempt could plausibly not hit: a
 * transport failure, or an error carrying a temporary server status
 * ({@link HttpStatusError} and the transports' typed HTTP errors expose
 * it as `status`).
 */
export function isTransientFailure(error: unknown): boolean {
    if (isTransientNetworkError(error)) {
        return true;
    }
    if (typeof error !== 'object' || error === null || !('status' in error)) {
        return false;
    }
    const { status } = error as { status: unknown };
    return typeof status === 'number' && isRetryableHttpStatus(status);
}

/** True when `headers` — a `Headers` or a plain record — carries `If-Match`. */
function hasIfMatch(headers: unknown): boolean {
    if (headers instanceof Headers) {
        return headers.has('If-Match');
    }
    return (
        typeof headers === 'object' &&
        headers !== null &&
        Object.keys(headers).some((name) => name.toLowerCase() === 'if-match')
    );
}

/**
 * The policy a request gets when its call site states none.
 *
 * GET/HEAD/OPTIONS are safe reads and PUT is idempotent by contract, so
 * replaying them cannot duplicate work. So is a version-guarded request —
 * one carrying `If-Match` — whatever its method: the replay of a write
 * that already landed is refused as stale rather than applied again. Any
 * other POST, PATCH or DELETE sends one attempt; a call site opts in with
 * `retryPolicy: 'transient'` after establishing that its handling of the
 * response tolerates a duplicate delivery.
 *
 * @param method - The request method.
 * @param headers - The request headers, checked for `If-Match`.
 */
export function defaultRetryPolicy(
    method: string,
    headers?: unknown
): RetryPolicy {
    const normalized = method.toUpperCase();
    const idempotent =
        normalized === 'GET' ||
        normalized === 'HEAD' ||
        normalized === 'OPTIONS' ||
        normalized === 'PUT';
    return idempotent || hasIfMatch(headers) ? 'transient' : 'none';
}

/**
 * The error a transport reports for a request that produced no HTTP
 * response: a {@link NetworkTransportError} for a failure a reconnect
 * could fix, `error` itself for anything else (a cancellation, a request
 * that could not be built).
 */
export function asTransportFailure(error: unknown): unknown {
    if (!isTransientNetworkError(error)) {
        return error;
    }
    return new NetworkTransportError(
        error instanceof Error
            ? `Network error (${error.message})`
            : 'Network error'
    );
}

/**
 * Exponential backoff with full jitter. The jitter matters here: when
 * a network change kills every in-flight request at once, un-jittered
 * retries would reconnect in lockstep and collide again.
 */
export function backoffDelayMs(
    attempt: number,
    randomFraction: () => number = Math.random
): number {
    const exponential = Math.min(
        MAX_BACKOFF_MS,
        INITIAL_BACKOFF_MS * 2 ** (attempt - 1)
    );
    return Math.round(exponential * (0.5 + 0.5 * randomFraction()));
}

/**
 * The server-requested wait carried by a retryable 429/503, in
 * milliseconds, or undefined when there is none. Only the
 * delay-seconds form is read; an HTTP-date is ignored.
 */
function retryAfterMs(error: unknown): number | undefined {
    if (
        !(error instanceof RetryableResponseError) ||
        !RETRY_AFTER_STATUS_CODES.has(error.response.status)
    ) {
        return undefined;
    }
    const header = error.response.headers.get('Retry-After')?.trim();
    if (!header || !/^\d+$/.test(header)) {
        return undefined;
    }
    return Number(header) * 1_000;
}

/**
 * A PHI-free name for a failure: the HTTP status of a retryable
 * response, otherwise the error's class name. Never the message —
 * transport and token errors can echo request details.
 */
function describeFailure(error: unknown): string {
    if (error instanceof RetryableResponseError) {
        return `HTTP ${error.response.status}`;
    }
    return error instanceof Error ? error.name : typeof error;
}

/** Logs one scheduled retry of the request named by `label`. */
function warnOnRetry(
    label: string,
    { attempt, delayMs, error }: RetryAttemptInfo
): void {
    console.warn(
        `${label} failed on attempt ${attempt} ` +
            `(${describeFailure(error)}); retrying in ${delayMs} ms.`
    );
}

export type BackoffSleepOptions = {
    /**
     * Whether the browser's `online` event may end the wait early. False
     * for a wait the server asked for (`Retry-After`): connectivity
     * returning says nothing about the server's load.
     */
    wakeOnOnline: boolean;
};

/**
 * The backoff wait: abortable, and cut short when connectivity returns —
 * a network change is the most common reason to be waiting here, and
 * once the new link is up there is no point sitting out the backoff. A
 * wait cut short still pauses for a random fraction of
 * {@link ONLINE_WAKE_SPREAD_MS}, so the requests it woke together do not
 * all reconnect in the same instant.
 */
async function backoffSleep(
    delayMs: number,
    signal?: AbortSignal,
    { wakeOnOnline }: BackoffSleepOptions = { wakeOnOnline: true }
): Promise<void> {
    const startedAt = Date.now();
    await sleep(delayMs, signal, { wakeOnOnline });
    const remainingMs = delayMs - (Date.now() - startedAt);
    if (remainingMs > 0) {
        await sleep(
            Math.min(remainingMs, Math.random() * ONLINE_WAKE_SPREAD_MS),
            signal
        );
    }
}

/**
 * The base a relative request URL resolves against: the page's address,
 * as `fetch` resolves it. Outside a document there is none, and any base
 * serves, since only the URL's own syntax is being checked.
 */
function requestUrlBase(): string {
    return globalThis.location?.href ?? 'http://localhost/';
}

/**
 * Builds one attempt's request and checks what the browser would reject
 * before sending. Fails with {@link RequestConstructionError} instead of
 * the platform's `TypeError`, which would otherwise read as a transport
 * failure — retried for the whole window and reported as a network
 * problem. Other errors (a token request that failed on the network)
 * pass through for the caller's classification.
 */
async function buildValidRequest(
    buildRequest: (attempt: number) => Promise<FetchRequest>,
    attempt: number
): Promise<FetchRequest> {
    try {
        const request = await buildRequest(attempt);
        // Each throws a TypeError for a URL, or a header name or value,
        // that fetch would refuse.
        new URL(request.url, requestUrlBase());
        new Headers(request.init.headers);
        return request;
    } catch (error) {
        if (error instanceof TypeError) {
            throw new RequestConstructionError(error);
        }
        throw error;
    }
}

/**
 * Runs `operation`, retrying it while it fails with an error the
 * `isTransient` predicate accepts, until the retry window closes.
 *
 * Cancellation wins over retrying: an aborted signal stops the wait
 * and rethrows immediately, and an `AbortError` is never treated as
 * transient.
 *
 * @param operation - The attempt to run, given its 1-based index; must
 *     be safe to repeat.
 * @param options - Retry window, cancellation and observability hooks.
 * @returns The first successful result.
 * @throws The last error once the window or the attempt budget is
 *     exhausted, the error unchanged when it is not transient, or an
 *     `AbortError`.
 */
export async function withTransientRetry<TResult>(
    operation: (attempt: number) => Promise<TResult>,
    options: TransientRetryOptions = {}
): Promise<TResult> {
    const {
        retryWindowMs = RETRY_WINDOW_MS,
        maxAttempts = MAX_ATTEMPTS,
        signal,
        isTransient = isTransientNetworkError,
        onRetry,
        randomFraction = Math.random,
        sleep: wait = backoffSleep,
        now = Date.now,
    } = options;

    if (!Number.isInteger(maxAttempts) || maxAttempts < 1) {
        throw new Error(
            `withTransientRetry: maxAttempts must be a positive integer, ` +
                `got ${maxAttempts}.`
        );
    }
    if (!(retryWindowMs >= 0)) {
        throw new Error(
            `withTransientRetry: retryWindowMs must be non-negative, ` +
                `got ${retryWindowMs}.`
        );
    }

    let firstFailureAt: number | undefined = undefined;
    for (let attempt = 1; ; attempt++) {
        throwIfAborted(signal);
        try {
            return await operation(attempt);
        } catch (error) {
            if (
                attempt >= maxAttempts ||
                isAbortError(error) ||
                !isTransient(error)
            ) {
                throw error;
            }
            const backoff = backoffDelayMs(attempt, randomFraction);
            const requested = retryAfterMs(error);
            const delayMs =
                requested === undefined
                    ? backoff
                    : Math.min(MAX_BACKOFF_MS, Math.max(backoff, requested));
            firstFailureAt ??= now();
            if (now() - firstFailureAt + delayMs > retryWindowMs) {
                throw error;
            }
            onRetry?.({ attempt, delayMs, error });
            // A wait the server asked for is not cut short by the browser
            // coming online; like any backoff it is capped at MAX_BACKOFF_MS.
            await wait(delayMs, signal, {
                wakeOnOnline: requested === undefined,
            });
        }
    }
}

/**
 * The response with its body routed through a pass-through stream that
 * reports every chunk as it arrives, so a reader of any kind — a buffer,
 * a `.blob()` — feeds the inactivity timeout without an extra copy.
 *
 * @param response - The live response.
 * @param onChunk - Called for every chunk received.
 */
function trackBody(response: Response, onChunk: () => void): Response {
    if (
        NULL_BODY_STATUSES.has(response.status) ||
        response.body === null ||
        response.body === undefined
    ) {
        // Nothing to stream: a null body, or a response stand-in without
        // a stream.
        return response;
    }
    const tracked = response.body.pipeThrough(
        new TransformStream<Uint8Array, Uint8Array>({
            transform(chunk, controller) {
                onChunk();
                controller.enqueue(chunk);
            },
        })
    );
    return new Response(tracked, responseInitOf(response));
}

function responseInitOf(response: Response): ResponseInit {
    return {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
    };
}

/**
 * Reads the whole response body into memory and returns an equivalent
 * response backed by it, so every later `.json()`/`.blob()`/`.text()`
 * is a memory read that cannot fail on the network or hang on a
 * stalled socket. The chunks are handed on as they arrived rather than
 * concatenated, so a download is not copied an extra time here; a
 * caller that wants one `Blob` of a large body should read it as one
 * inside the attempt instead ({@link fetchBlobWithRetry}).
 *
 * @param response - The response to buffer.
 */
export async function bufferResponse(response: Response): Promise<Response> {
    const init = responseInitOf(response);
    if (NULL_BODY_STATUSES.has(response.status) || response.body === null) {
        return new Response(null, init);
    }
    if (response.body === undefined) {
        // A response stand-in without a stream.
        return new Response(await response.blob(), init);
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    for (;;) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        chunks.push(value);
    }
    return new Response(
        new ReadableStream<Uint8Array>({
            start(controller) {
                // Emptied as it is handed on, so a chunk is released once
                // the reader has taken it rather than held for the life of
                // the response.
                for (const chunk of chunks.splice(0)) {
                    controller.enqueue(chunk);
                }
                controller.close();
            },
        }),
        init
    );
}

/**
 * `fetch` with retries for transport failures and temporary server
 * errors ({@link isRetryableHttpStatus}).
 *
 * Each attempt reads the response body before the attempt counts as
 * done, so a network change while the body streams is retried like
 * any other transport failure, and the returned response is fully
 * buffered.
 *
 * The request is rebuilt for every attempt rather than reused, so each
 * retry carries a freshly built body and freshly resolved headers — a
 * long-running upload can outlive the access token that started it.
 *
 * Only requests that are safe to repeat may be sent through this
 * helper with more than one attempt: a retry can duplicate work
 * server-side when a response is lost after the server has already
 * processed the request.
 *
 * Every scheduled retry is logged under `options.label`, and the request
 * counts as retrying in the connection state (`connectionState.ts`) from
 * its first retry until it settles.
 *
 * @param buildRequest - Builds the URL and `RequestInit` for one
 *     attempt, given its 1-based index.
 * @param options - Request name, retry policy and window, per-attempt
 *     deadline, cancellation and observability hooks.
 * @returns The successful response, or the final error response once
 *     retrying stops on a retryable status; either way buffered.
 * @throws The transport error once retrying stops, or an `AbortError`
 *     when cancelled.
 */
export function fetchWithRetry(
    buildRequest: (attempt: number) => Promise<FetchRequest>,
    options: FetchRetryOptions
): Promise<Response> {
    return fetchAndReadWithRetry(buildRequest, options, bufferResponse);
}

/** Reads a response's body inside the attempt that received it. */
export type BodyReader<T> = (response: Response) => Promise<T>;

/**
 * What {@link fetchBlobWithRetry} returns: the response, fully read,
 * and its body as one `Blob` when its status is 200.
 */
export type BlobResult = {
    /** The response; its body is consumed. */
    response: Response;
    /** The body, for a 200 response only. */
    blob: Blob | undefined;
};

/**
 * {@link fetchWithRetry} for a download: a 200 body is read as one
 * `Blob` inside the attempt, so a large download is held once — the
 * `Blob` the caller gets — rather than buffered and then copied into a
 * `Blob`. Any other response is buffered for the caller to map.
 */
export function fetchBlobWithRetry(
    buildRequest: (attempt: number) => Promise<FetchRequest>,
    options: FetchRetryOptions
): Promise<BlobResult> {
    return fetchAndReadWithRetry(buildRequest, options, readBlob);
}

/** The body reader behind {@link fetchBlobWithRetry}. */
export const readBlob: BodyReader<BlobResult> = async (response) =>
    response.status === 200
        ? { response, blob: await response.blob() }
        : { response: await bufferResponse(response), blob: undefined };

/**
 * {@link fetchWithRetry} with the body read by `readBody` inside each
 * attempt, under the attempt's inactivity timeout, and the reader's
 * result returned. A reader that throws fails the attempt like a lost
 * connection does; a transport failure thrown by it is retried.
 *
 * @param readBody - Reads the response it is given. It is called for
 *     every response, a retryable status included, so that body is read
 *     within the attempt too.
 * @returns What `readBody` made of the successful response, or of the
 *     final response once retrying stops on a retryable status.
 */
export async function fetchAndReadWithRetry<T>(
    buildRequest: (attempt: number) => Promise<FetchRequest>,
    options: FetchRetryOptions,
    readBody: BodyReader<T>
): Promise<T> {
    // Assigned from the retry hook, so declared without an initialiser:
    // an initial `undefined` would narrow the type at the `catch`.
    let settleRetrying: ((outcome: RetryOutcome) => void) | undefined;
    try {
        return await withTransientRetry<T>(
            async (attempt: number) => {
                const { response, value } = await fetchAttempt(
                    buildRequest,
                    attempt,
                    options,
                    readBody
                );
                if (isRetryableHttpStatus(response.status)) {
                    throw new RetryableResponseError(response, value);
                }
                return value;
            },
            {
                ...options,
                maxAttempts: options.retryPolicy === 'none' ? 1 : undefined,
                isTransient: (error: unknown) =>
                    error instanceof RetryableResponseError ||
                    isTransientNetworkError(error),
                onRetry: (info: RetryAttemptInfo) => {
                    settleRetrying ??= beginRetrying();
                    warnOnRetry(options.label, info);
                    options.onRetry?.(info);
                },
            }
        );
    } catch (error) {
        // Only a request that got its answer counts as the connection
        // coming back: one that gave up on a server still down, or was
        // cancelled while retrying, must not read as restored.
        settleRetrying?.(isAbortError(error) ? 'cancelled' : 'failed');
        settleRetrying = undefined;
        if (error instanceof RetryableResponseError) {
            // Retrying stopped on a server error: hand the last response
            // back so the caller reports its status and message through
            // its normal error path.
            return error.value as T;
        }
        throw error;
    } finally {
        settleRetrying?.('succeeded');
    }
}

/**
 * Settles with `work`, or fails once `timeoutMs` passes first — with a
 * `TimeoutError` — or the caller aborts — with an `AbortError`. For
 * work that takes no signal of its own (a token request): it is
 * abandoned, not cancelled.
 */
async function settleWithin<T>(
    work: Promise<T>,
    what: string,
    timeoutMs: number,
    caller?: AbortSignal
): Promise<T> {
    const deadline = startDeadline(timeoutMs, caller);
    try {
        return await new Promise<T>((resolve, reject) => {
            const onAbort = (): void =>
                reject(
                    deadline.expired()
                        ? timeoutError(what, timeoutMs)
                        : abortError()
                );
            // Observed whatever happens below, so work abandoned to a
            // timeout or a cancellation cannot surface later as an
            // unhandled rejection.
            work.then(resolve, reject).finally(() =>
                deadline.signal.removeEventListener('abort', onAbort)
            );
            if (deadline.signal.aborted) {
                onAbort();
                return;
            }
            deadline.signal.addEventListener('abort', onAbort, { once: true });
        });
    } finally {
        deadline.clear();
    }
}

/**
 * Runs one attempt — build, send, read the body — under the attempt's
 * timeouts: the response timeout until the headers are in (building
 * the request included), then the inactivity timeout while the body
 * streams. A timeout surfaces as a `TimeoutError` (transient) while a
 * caller abort stays an `AbortError` (never retried).
 */
async function fetchAttempt<T>(
    buildRequest: (attempt: number) => Promise<FetchRequest>,
    attempt: number,
    options: FetchRetryOptions,
    readBody: BodyReader<T>
): Promise<{ response: Response; value: T }> {
    const baseTimeoutMs =
        options.responseTimeoutMs ?? DEFAULT_RESPONSE_TIMEOUT_MS;

    const { url, init } = await settleWithin(
        buildValidRequest(buildRequest, attempt),
        'Preparing the request',
        baseTimeoutMs,
        options.signal
    );

    // The request is built asynchronously, so a cancellation can land
    // between the loop's own check and here.
    const caller = init.signal ?? options.signal;
    throwIfAborted(caller);

    const responseTimeoutMs = responseTimeoutFor(init.body, baseTimeoutMs);
    const deadline = startDeadline(responseTimeoutMs, caller);
    let phase = 'Waiting for the response';
    let phaseTimeoutMs = responseTimeoutMs;
    try {
        const response = trackBody(
            await fetch(url, { ...init, signal: deadline.signal }),
            () => deadline.restart(BODY_IDLE_TIMEOUT_MS)
        );
        phase = 'Receiving the response';
        phaseTimeoutMs = BODY_IDLE_TIMEOUT_MS;
        deadline.restart(BODY_IDLE_TIMEOUT_MS);
        return { response, value: await readBody(response) };
    } catch (error) {
        if (deadline.expired() && !caller?.aborted) {
            throw timeoutError(phase, phaseTimeoutMs);
        }
        throw error;
    } finally {
        deadline.clear();
    }
}

/**
 * How long a polling loop keeps polling through consecutive transient
 * failures before giving up on the job. Long enough for a network switch
 * or a short connector restart; a job still running server-side is worth
 * that wait, since abandoning it strands its result.
 */
export const POLL_OUTAGE_TOLERANCE_MS = 5 * 60 * 1_000;

export type OutageTolerance = {
    /**
     * Records a failed poll. True when the failure is transient
     * ({@link isTransientFailure}) and the outage it belongs to has not
     * yet lasted `maxOutageMs`, i.e. the caller should poll again. False
     * ends the outage as a failure.
     */
    tolerate: (error: unknown) => boolean;
    /** Records a successful poll, ending any outage as recovered. */
    reset: () => void;
    /**
     * Ends any outage without a verdict, when the loop stops for a
     * reason other than a poll's outcome: a cancellation, or the loop's
     * own overall timeout. Safe to call when no outage is open.
     */
    end: () => void;
};

/**
 * Outage bookkeeping for a polling loop. A loop that polls a
 * long-running job must not abandon the job over a network blip — the
 * job keeps running server-side, and giving up on it strands its
 * result — but it must not poll a dead backend forever either. The
 * loop's own interval is the retry spacing, so the poll request itself
 * should be sent with `retryPolicy: 'none'`.
 *
 * An outage is reported to the connection state like a retrying
 * request, from its first tolerated failure until it ends, so the UI
 * says the connection is being re-established while a job is followed
 * through an interruption.
 *
 * @param maxOutageMs - How long consecutive transient failures are
 *     tolerated before the loop gives up.
 * @param now - Clock in milliseconds; injectable for tests.
 */
export function createOutageTolerance(
    maxOutageMs: number,
    now: () => number = Date.now
): OutageTolerance {
    let outageStartedAt: number | undefined = undefined;
    let settleRetrying: ((outcome: RetryOutcome) => void) | undefined =
        undefined;
    const endOutage = (outcome: RetryOutcome): void => {
        outageStartedAt = undefined;
        settleRetrying?.(outcome);
        settleRetrying = undefined;
    };
    return {
        tolerate: (error: unknown): boolean => {
            if (!isTransientFailure(error)) {
                endOutage(isAbortError(error) ? 'cancelled' : 'failed');
                return false;
            }
            outageStartedAt ??= now();
            settleRetrying ??= beginRetrying();
            if (now() - outageStartedAt < maxOutageMs) {
                return true;
            }
            endOutage('failed');
            return false;
        },
        reset: (): void => endOutage('succeeded'),
        end: (): void => endOutage('cancelled'),
    };
}
